import { AxiosResponse } from "axios";
import { crudService } from "services/crud.service";
import { initialTableState } from "store/constants";
import { NotificationManager } from "react-notifications";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  dataManipulationForAdminLeadTableState,
  dataManipulationForCallRecordTableState,
  dataManipulationForIndividualPropertyTableState,
  dataManipulationForPolishedLeadTableState,
  dataManipulationForPropertyTableState,
  dataManipulationForRawLeadTableState,
  dataManipulationForSalesTableState,
  dataManipulationForStaffTargetTableState,
  dataManipulationForTaskTableState,
  dataManipulationForTokenTableState,
  getErrorMessage,
  getPaginatedUrlWithSearchParams,
} from "store/helper";
import { endPoints } from "utils/constants";

const {
  rawLeadTableStateEndPoint,
  polishLeadTableStateEndPoint,
  taskTableStateEndPoint,
  callRecordTableStateEndPoint,
  exportLeadTableStateEndPoint,
  propertyUnitTableStatEndPoint,
  tokenTableStateEndPoint,
  staffTargetTableStateEndPoint,
  individualPropertyTableStateEndPoint,
  salesTableStateEndPoint,
} = endPoints;

export const fetchRawLeadTableState = createAsyncThunk(
  "tableStates/fetchRawLeadTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: rawLeadTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRawLeadTableState = createAsyncThunk(
  "tableStates/updateRawLeadTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${rawLeadTableStateEndPoint}`,
        tableId,
        updatedTableState
      );

      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updatePolishLeadTableState = createAsyncThunk(
  "tableStates/updatePolishLeadTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${polishLeadTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPolishLeadTableState = createAsyncThunk(
  "tableStates/fetchPolishLeadTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: polishLeadTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTaskTableState = createAsyncThunk(
  "tableStates/fetchTaskTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: taskTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTaskTableState = createAsyncThunk(
  "tableStates/updateTaskTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${taskTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCallRecordTableState = createAsyncThunk(
  "tableStates/fetchCallRecordTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: callRecordTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCallRecordTableState = createAsyncThunk(
  "tableStates/updateCallRecordTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${callRecordTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateAdminLeadTableState = createAsyncThunk(
  "tableStates/updateAdminLeadTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${exportLeadTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAdminLeadTableState = createAsyncThunk(
  "tableStates/fetchAdminLeadTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: exportLeadTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAPropertyUnitTableState = createAsyncThunk(
  "tableStates/updateAPropertyTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${propertyUnitTableStatEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPropertyUnitTableState = createAsyncThunk(
  "tableStates/fetchPropertyUnitTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: propertyUnitTableStatEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateIndividualPropertyUnitTableState = createAsyncThunk(
  "tableStates/updateIndividualPropertyUnitTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${individualPropertyTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchIndividualPropertyUnitTableState = createAsyncThunk(
  "tableStates/fetchIndividualPropertyUnitTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: individualPropertyTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTokenTableState = createAsyncThunk(
  "tableStates/updateTokenTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${tokenTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTokenTableState = createAsyncThunk(
  "tableStates/fetchTokenTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: tokenTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateStaffTargetTableState = createAsyncThunk(
  "tableStates/updateStaffTargetTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${staffTargetTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStaffTargetTableState = createAsyncThunk(
  "tableStates/fetchStaffTargetTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: staffTargetTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSalesTableState = createAsyncThunk(
  "tableStates/fetchSalesTableState",
  async (
    { page = 1, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: salesTableStateEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSalesTableState = createAsyncThunk(
  "tableStates/updateSalesTableState",
  async ({ updatedTableState, tableId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${salesTableStateEndPoint}`,
        tableId,
        updatedTableState
      );
      if (response.status === 204) {
        NotificationManager.success("View Updated Successfully");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const tableStateSlice = createSlice({
  name: "tableStates",
  initialState: initialTableState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch raw lead table state
    builder
      .addCase(fetchRawLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchRawLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["raw-lead"] = dataManipulationForRawLeadTableState(
            action.payload?.objects[0]
          );
        }
      )
      .addCase(fetchRawLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update raw lead table state
    builder
      .addCase(updateRawLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateRawLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateRawLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch polish lead table state
    builder
      .addCase(fetchPolishLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchPolishLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["polished-lead"] =
            dataManipulationForPolishedLeadTableState(
              action.payload?.objects[0]
            );
        }
      )
      .addCase(fetchPolishLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update polish lead table state
    builder
      .addCase(updatePolishLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updatePolishLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updatePolishLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch task table state
    builder
      .addCase(fetchTaskTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchTaskTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["task"] = dataManipulationForTaskTableState(
            action.payload?.objects[0]
          );
        }
      )
      .addCase(fetchTaskTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update task table state
    builder
      .addCase(updateTaskTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateTaskTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateTaskTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch call record table state
    builder
      .addCase(fetchCallRecordTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchCallRecordTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["call-record"] =
            dataManipulationForCallRecordTableState(action.payload?.objects[0]);
        }
      )
      .addCase(fetchCallRecordTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch admin lead table state
    builder
      .addCase(fetchAdminLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAdminLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["admin-lead"] =
            dataManipulationForAdminLeadTableState(action.payload?.objects[0]);
        }
      )
      .addCase(fetchAdminLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update admin lead table state
    builder
      .addCase(updateAdminLeadTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateAdminLeadTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateAdminLeadTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch property unit table state
    builder
      .addCase(fetchPropertyUnitTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchPropertyUnitTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["property-unit"] =
            dataManipulationForPropertyTableState(action.payload?.objects[0]);
        }
      )
      .addCase(fetchPropertyUnitTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update property unit table state
    builder
      .addCase(updateAPropertyUnitTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateAPropertyUnitTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateAPropertyUnitTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch individual property unit table state
    builder
      .addCase(fetchIndividualPropertyUnitTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchIndividualPropertyUnitTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["individual-property-unit"] =
            dataManipulationForIndividualPropertyTableState(
              action.payload?.objects[0]
            );
        }
      )
      .addCase(
        fetchIndividualPropertyUnitTableState.rejected,
        (state: any, action) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // update individual property unit table state
    builder
      .addCase(updateIndividualPropertyUnitTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateIndividualPropertyUnitTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateIndividualPropertyUnitTableState.rejected,
        (state: any, action) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch property token table state
    builder
      .addCase(fetchTokenTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchTokenTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["token"] = dataManipulationForTokenTableState(
            action.payload?.objects[0]
          );
        }
      )
      .addCase(fetchTokenTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update property token table state
    builder
      .addCase(updateTokenTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateTokenTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateTokenTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch staff target table state
    builder
      .addCase(fetchStaffTargetTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchStaffTargetTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["staff-target"] =
            dataManipulationForStaffTargetTableState(
              action.payload?.objects[0]
            );
        }
      )
      .addCase(fetchStaffTargetTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update staff target table state
    builder
      .addCase(updateStaffTargetTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateStaffTargetTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateStaffTargetTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch sales table state
    builder
      .addCase(fetchSalesTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchSalesTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.tableStates["sales"] = dataManipulationForSalesTableState(
            action.payload?.objects[0]
          );
        }
      )
      .addCase(fetchSalesTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // update sales table state
    builder
      .addCase(updateSalesTableState.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateSalesTableState.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(updateSalesTableState.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const tableStatesReducer = tableStateSlice.reducer;
